import React from "react";
import { useTranslation } from "react-i18next";

export default function DatabaseKpiStrip({ database, indexesCount }) {
  const { t } = useTranslation();
  const iconMap = {
    documents: "📄",
    storage: "💾",
    ingest: "⬇︎",
    replicas: "⧉",
    partitions: "▤",
    health: "❤",
    indexes: "#",
  };

  const kpis = [
    {
      key: "documents",
      label: t("admin.kpi.documents", { defaultValue: "Documents" }),
      value: database?.documentCount != null
        ? database.documentCount.toLocaleString()
        : (database?.Documents ?? database?.docs ?? "—"),
    },
    {
      key: "storage",
      label: t("admin.kpi.storage", { defaultValue: "Storage" }),
      value:
        database?.storageSizeHuman ??
        database?.storageSize ??
        database?.Storage ??
        "—",
    },
    {
      key: "ingest",
      label: t("admin.kpi.ingest", { defaultValue: "Ingest rate" }),
      value: database?.ingestRate ?? database?.IngestRate ?? "—",
    },
    {
      key: "replicas",
      label: t("admin.kpi.replicas", { defaultValue: "Replicas" }),
      value: database?.replicas ?? database?.Replicas ?? "—",
    },
    {
      key: "partitions",
      label: t("admin.kpi.partitions", { defaultValue: "Partitions" }),
      // Real physical partition count. The backend reports a non-partitioned table as a
      // single segment (1), so this is always a concrete number that grows to the real N
      // once the table is converted to a monthly-partitioned scheme.
      value: (database?.partitionCount ?? database?.partitions ?? 1).toLocaleString(),
    },
    {
      key: "health",
      label: t("admin.kpi.health", { defaultValue: "Health" }),
      value: database?.health ?? database?.Health ?? "—",
    },
  ];

  return (
    <div className="db-kpi-strip">
      {kpis.map((kpi) => {
        const icon = iconMap[kpi.key];
        const value = kpi.value ?? "—";
        const isHealth = kpi.key === "health";
        let healthState = "";
        if (isHealth && typeof value === "string") {
          const v = value.toLowerCase();
          if (v.includes("healthy")) healthState = "ok";
          else if (v.includes("warn") || v.includes("degrad"))
            healthState = "warn";
          else if (v && value !== "—") healthState = "bad";
        }
        return (
          <div key={kpi.key} className="db-kpi-card">
            <div className="db-kpi-header">
              {icon && (
                <div
                  className={
                    "db-kpi-icon db-kpi-icon--" +
                    kpi.key +
                    (isHealth && healthState
                      ? " db-kpi-icon--health-" + healthState
                      : "")
                  }
                >
                  <span className="db-kpi-icon-glyph">{icon}</span>
                </div>
              )}
              <div className="db-kpi-label">{kpi.label}</div>
            </div>
            <div
              className={
                "db-kpi-value" +
                (isHealth && healthState
                  ? " db-kpi-value--health-" + healthState
                  : "")
              }
            >
              {value}
            </div>
          </div>
        );
      })}
      <div className="db-kpi-card db-kpi-card--indexes">
        <div className="db-kpi-header">
          <div className="db-kpi-icon db-kpi-icon--indexes">
            <span className="db-kpi-icon-glyph">#</span>
          </div>
          <div className="db-kpi-label">
            {t("admin.indexesShortLabel", { defaultValue: "Indexes" })}
          </div>
        </div>
        <div className="db-kpi-value">{indexesCount ?? 0}</div>
      </div>
    </div>
  );
}
